import React, { useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';

// ─── Arabic normalization ────────────────────────────────────────────────────
// Removes diacritics + unifies hamza/alef/ya/ta-marbuta variants so users can
// type "امريكا" / "أمريكا" / "إمريكا" interchangeably and still match.
const TASHKEEL = /[\u064B-\u0652\u0670\u0640]/g; // fathatan..sukun, dagger alef, tatweel
const arNorm = (s = '') =>
  s
    .toString()
    .normalize('NFC')
    .replace(TASHKEEL, '')
    .replace(/[\u0622\u0623\u0625]/g, '\u0627') // آ أ إ → ا
    .replace(/\u0649/g, '\u064A')               // ى → ي
    .replace(/\u0629/g, '\u0647')               // ة → ه
    .replace(/\u0624/g, '\u0648')               // ؤ → و
    .replace(/\u0626/g, '\u064A')               // ئ → ي
    .toLowerCase()
    .trim();

// Custom cmdk filter — returns a relevance score in [0,1]. 0 = no match.
const arabicFilter = (value, search, keywords = []) => {
  const sN = arNorm(search);
  if (!sN) return 1; // empty query matches all
  const haystacks = [value, ...keywords].map(arNorm);
  for (const h of haystacks) {
    if (h.startsWith(sN)) return 1;     // best match
    if (h.includes(sN)) return 0.5;     // partial match
  }
  return 0;
};

/**
 * Searchable country dropdown — drop-in replacement for the previous shadcn <Select>.
 *
 * Props
 * • countries  : Array<{ id, name }>            — full catalog to choose from
 * • value      : string                         — selected country name ('' means none)
 * • onChange   : (name: string) => void         — called with the new selection ('' to clear)
 * • placeholder, error, testId                   — UX bits
 */
function SearchableCountrySelect({
  countries = [],
  value = '',
  onChange,
  placeholder = 'اختر الدولة',
  error = false,
  testId = 'country-select',
}) {
  const [open, setOpen] = useState(false);

  const triggerClass = `flex items-center justify-between gap-2 h-10 w-full rounded-md border bg-background px-3 py-2 text-sm text-right transition focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
    error ? 'border-destructive ring-1 ring-destructive/40' : 'border-input'
  }`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={triggerClass} data-testid={testId}>
          <span className={value ? '' : 'text-muted-foreground'}>
            {value || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] overflow-hidden pointer-events-auto"
        align="start"
        sideOffset={4}
        collisionPadding={16}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command filter={arabicFilter} className="max-h-[300px]">
          <div className="flex items-center gap-2 border-b px-3 py-2 shrink-0">
            <Search className="h-4 w-4 text-muted-foreground" />
            <CommandInput
              placeholder="ابحث عن دولة..."
              className="h-9 border-0 p-0 focus:ring-0 text-right"
              data-testid={`${testId}-search`}
            />
          </div>
          <CommandList
            className="overflow-y-auto overflow-x-hidden overscroll-contain"
            style={{
              maxHeight: '240px',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
              لا توجد دولة بهذا الاسم
            </CommandEmpty>
            <CommandGroup className="p-1">
              <CommandItem
                value="__none__"
                keywords={['بدون', 'لا شيء', 'none']}
                onSelect={() => { onChange(''); setOpen(false); }}
                data-testid={`${testId}-opt-none`}
              >
                <Check className={`me-2 h-4 w-4 ${value === '' ? 'opacity-100' : 'opacity-0'}`} />
                — بدون تحديد —
              </CommandItem>
              {countries.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={(v) => { onChange(v); setOpen(false); }}
                  data-testid={`${testId}-opt-${c.id}`}
                >
                  <Check className={`me-2 h-4 w-4 ${value === c.name ? 'opacity-100' : 'opacity-0'}`} />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// React.memo — the country list is a heavy child (200+ CommandItems). Without
// memoization, every keystroke in a sibling input (customer_name, activity...)
// re-runs this component's JSX. Shallow comparison of countries/value/onChange
// keeps typing snappy on TVs and low-end phones.
export default React.memo(SearchableCountrySelect);
